'use client';
import { CheckIcon } from '@heroicons/react/20/solid';
import {
    CheckCircleIcon,
    DocumentDuplicateIcon,
    GlobeAltIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { ConfirmationModal } from '../ConfirmationModal';
import { LoadingSpinner } from '../LoadingSpinner';
import { useEditor } from '../context/EditorContext/EditorContext';
import { API } from '../utils/api';

export function AddDomain() {
    const editor = useEditor();

    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState(false);
    const [domain, setDomain] = useState(editor.global.data.domain || '');
    const [initial_domain, setInitialDomain] = useState(
        editor.global.data.domain || ''
    );

    useEffect(() => {
        setLoading(true);

        const checkDomainStatus = async () => {
            const response = await API.get(
                `/${editor.global.data.company_id}/domain/status`,
                {
                    params: {
                        domain,
                    },
                }
            );

            if (response.status === 'active') {
                setActive(true);
            } else {
                setActive(false);
            }

            setLoading(false);
        };

        if (open) {
            const interval = setInterval(checkDomainStatus, 4000);

            return () => clearInterval(interval);
        }
    }, [open, active, domain]);

    useEffect(() => {
        if (copied) {
            setTimeout(() => {
                setCopied(false);
            }, 2500);
        }
    }, [copied]);

    const handleCopy = () => {
        navigator.clipboard.writeText('76.76.21.21');

        setCopied(true);
    };

    const handleConfirm = async () => {
        if (domain !== initial_domain) {
            setConfirming(true);
            try {
                await API.post(`/${editor.global.data.company_id}/domain`, {
                    domain,
                });

                setInitialDomain(domain);

                setError(false);
                setOpen(false);
            } catch (error) {
                setError(true);
            }
            setConfirming(false);
        } else {
            setOpen(false);
        }
    };

    return (
        <>
            <button
                className="w-32 rounded-lg border-2 border-blue-500 p-2 text-blue-500 transition-all ease-in hover:border-blue-800 hover:text-blue-800"
                onClick={() => setOpen(true)}
            >
                Add Domain
            </button>
            <ConfirmationModal
                title="Add My Domain"
                variant="info"
                size="large"
                message={
                    <div className="mt-4 flex flex-col gap-2">
                        <p className="text-md text-center text-gray-500">
                            What domain name would you like for your new
                            website?
                        </p>

                        <div>
                            <label
                                className="mb-1 block select-none text-left text-sm font-semibold text-gray-700"
                                htmlFor="publish"
                            >
                                Domain
                            </label>
                            <div className="pb-4">
                                <input
                                    id="domain"
                                    className={`focus:shadow-outline w-full appearance-none rounded border px-3 py-2 leading-tight text-neutral-800 shadow focus:outline-none ${
                                        error
                                            ? 'border-red-500'
                                            : 'border-blue-500'
                                    }`}
                                    placeholder="www.example.com"
                                    value={domain}
                                    onChange={(event) =>
                                        setDomain(event.target.value)
                                    }
                                />

                                {error && (
                                    <p className="text-md pt-1 text-left text-red-600">
                                        Failed to add domain
                                    </p>
                                )}
                            </div>

                            <p className="text-md text-center text-gray-500">
                                To view your site on this domain, you will need
                                to add an <b>A Record</b> to your DNS settings.
                                This is a simple process that takes less than 5
                                minutes.{' '}
                                <a
                                    className="text-blue-600 hover:text-blue-700"
                                    href="https://help.nicejob.com"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Click here for step by step instructions to
                                    change your A Records
                                </a>
                                .
                            </p>

                            <label
                                className="mt-4 block select-none text-left text-sm font-semibold text-gray-700"
                                htmlFor="publish"
                            >
                                Your new A Record is:
                            </label>
                            <div
                                className="mt-1 flex w-full cursor-pointer items-center justify-between rounded-lg border border-gray-300 bg-gray-50 p-2 text-left text-sm text-gray-900"
                                onClick={handleCopy}
                            >
                                <p className="pl-2 font-semibold">
                                    76.76.21.21
                                </p>
                                <button
                                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                                    onClick={handleCopy}
                                >
                                    <span className="flex items-center gap-2">
                                        {copied ? (
                                            <>
                                                Copied
                                                <CheckIcon className="h-5 w-5" />
                                            </>
                                        ) : (
                                            <>
                                                Copy
                                                <DocumentDuplicateIcon className="h-5 w-5" />
                                            </>
                                        )}
                                    </span>
                                </button>
                            </div>

                            <label
                                className="mt-4 block select-none text-left text-sm font-semibold text-gray-700"
                                htmlFor="publish"
                            >
                                Domain Status:
                            </label>

                            <div className="mt-2 flex items-center justify-start gap-2">
                                {loading && (
                                    <>
                                        <LoadingSpinner />
                                        <p className="text-md text-center text-gray-500">
                                            Checking Domain Status
                                        </p>
                                    </>
                                )}

                                {!loading && active && (
                                    <>
                                        <CheckCircleIcon className="h-6 w-6 text-green-600" />
                                        <p className="text-md text-center text-gray-500">
                                            Valid Configuration
                                        </p>
                                    </>
                                )}

                                {!loading && !active && (
                                    <>
                                        <XCircleIcon className="h-6 w-6 text-red-600" />
                                        <p className="text-md text-center text-gray-500">
                                            Invalid Configuration
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                }
                prompt="Confirm domain"
                icon={<GlobeAltIcon className="h-6 w-6 " aria-hidden="true" />}
                open={open}
                onClose={() => setOpen(false)}
                onConfirm={handleConfirm}
                loading={confirming}
            />
        </>
    );
}
